/*! cro-anime — CRO Design System v1. Declarative anime.js (v4) motion for any element with [data-cro-anime="<preset>"].
 *  Load ONCE per page as a module (it imports the vendored ./cro-animejs.js from the same assets folder):
 *    <script type="module" src="{{ 'cro-anime.js' | asset_url }}"></script>
 *  and next to it (the engine then downloads in parallel with the wrapper, and the packager ships it):
 *    <link rel="modulepreload" href="{{ 'cro-animejs.js' | asset_url | split: '?' | first }}">
 *  The href must be the SAME URL the import below resolves to, so no ?v= (asset_url adds one): a versioned href makes the
 *  browser fetch the engine twice (measured 15-10-2026 on the Halden pages, Lumera: 2 requests, 1 after the fix).
 *
 *  PRESETS (value of data-cro-anime)
 *    fade-up      opacity 0→1 + rise from below (distance px)          fade-in     opacity 0→1
 *    scale-in     opacity 0→1 + scale .92→1                            underline   scaleX 0→1, origin left (accent bars)
 *    slide-left   enters FROM the left  (x −distance → 0)              slide-right enters FROM the right (x +distance → 0)
 *    stagger      the element's DIRECT CHILDREN fade-up one after another (parent stays put; children must not carry
 *                 their own data-cro-anime)
 *    count        counts the first number in the text from 0 to its value, keeps prefix/suffix ("$", "%", "+", "mg",
 *                 "/5") and thousands commas / decimals; locks min-width so the line never reflows
 *    draw         SVG stroke draw (svg.createDrawable) for path/line/polyline/polygon/circle/ellipse/rect inside (or the
 *                 element itself); stagger = ms between shapes
 *    float        gentle infinite bob, ±distance px (default 6), one half-cycle = duration (default 3200 ms); small chips/badges
 *    pulse-ring   subtle infinite ring behind a CTA badge: appends an absolutely positioned <span aria-hidden> that
 *                 scales out `distance` px (default 10) and fades; ring colour = --cro-anime-ring (default currentColor);
 *                 host must not clip (overflow:visible); a static host becomes position:relative
 *  OPTIONS (attributes on the same element, all optional)
 *    data-cro-anime-delay="ms" · data-cro-anime-duration="ms" · data-cro-anime-stagger="ms" · data-cro-anime-distance="px"
 *    data-cro-anime-once="true|false" (default true; false = hide again when it leaves the screen and replay on re-entry)
 *  BEHAVIOUR
 *    - The hidden start state is applied by THIS script (inline style) just before observing, never by CSS, so if the
 *      script or the engine fails to load, content stays visible. Only transform/opacity (and stroke dash for draw) move: no CLS.
 *    - IntersectionObserver (threshold ~15 %, bottom margin −8 %): entrance presets run once on first view; float/pulse-ring
 *      run while visible and pause off-screen. Tall containers trigger on a 20 %-viewport slice instead of 15 % of their height.
 *    - prefers-reduced-motion: reduce → final state straight away, no loops (also reacts when the setting changes live).
 *    - Print and failures settle everything to its final state. Theme editor: re-scans on shopify:section:load, cleans up on
 *      shopify:section:unload. The data attribute goes on a wrapper that has no transform of its own (anime writes `transform`).
 *  API for section scripts: window.croAnime = { animate, stagger, createTimeline, svg, utils, scan(root), reduced(), inView(el, cb) }. */
import { animate, stagger, createTimeline, svg, utils } from './cro-animejs.js';

(() => {
  const KEY = '__croAnime';
  if (window[KEY]) return;
  window[KEY] = true;

  const SEL = '[data-cro-anime]';
  const SHAPES = 'path,line,polyline,polygon,circle,ellipse,rect';
  const EASE = 'outCubic';
  const mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const reduced = () => !!(mq && mq.matches);
  const hasIO = 'IntersectionObserver' in window;
  const recs = new Map(); // element -> record
  const saved = new WeakMap(); // element -> inline styles before we touched them

  const num = (el, k, d) => {
    const v = parseFloat(el.getAttribute('data-cro-anime-' + k));
    return Number.isFinite(v) ? v : d;
  };
  const flag = (el, k, d) => {
    const v = el.getAttribute('data-cro-anime-' + k);
    return v == null ? d : !/^(false|0|no|off)$/i.test(v.trim());
  };

  // ---- per-preset defaults + hidden start state (entrance family) -------------------------------------------------
  const DEF = {
    base: { duration: 600, distance: 24, stagger: 80 },
    'slide-left': { distance: 40 }, 'slide-right': { distance: 40 },
    stagger: { distance: 20 }, underline: { duration: 700 }, count: { duration: 1400 },
    draw: { duration: 1200, stagger: 120 }, float: { duration: 3200, distance: 6 }, 'pulse-ring': { duration: 1800, distance: 10 },
  };
  const FROM = {
    'fade-up': (o) => ({ opacity: 0, y: o.distance }),
    'fade-in': () => ({ opacity: 0 }),
    'scale-in': () => ({ opacity: 0, scale: 0.92 }),
    'slide-left': (o) => ({ opacity: 0, x: -o.distance }),
    'slide-right': (o) => ({ opacity: 0, x: o.distance }),
    stagger: (o) => ({ opacity: 0, y: o.distance }),
    underline: () => ({ scaleX: 0 }),
  };
  const tf = (f) => [
    f.x != null && `translateX(${f.x}px)`, f.y != null && `translateY(${f.y}px)`,
    f.scale != null && `scale(${f.scale})`, f.scaleX != null && `scaleX(${f.scaleX})`,
  ].filter(Boolean).join(' ');

  const hide = (el, f) => {
    // keep the FIRST snapshot: anime's revert() re-applies the hidden inline state, so a later snapshot would be junk
    if (!saved.has(el)) saved.set(el, { o: el.style.opacity, t: el.style.transform, to: el.style.transformOrigin, w: el.style.willChange });
    if (f.opacity != null) el.style.opacity = String(f.opacity);
    const t = tf(f);
    if (t) el.style.transform = t;
    if (f.scaleX != null) el.style.transformOrigin = 'left center';
  };
  const restore = (el) => {
    const s = saved.get(el);
    if (!s) return;
    el.style.opacity = s.o; el.style.transform = s.t; el.style.transformOrigin = s.to; el.style.willChange = s.w;
  };

  // ---- count helpers -----------------------------------------------------------------------------------------------
  const textNodeWithNumber = (el) => {
    const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = w.nextNode(); n; n = w.nextNode()) if (/\d/.test(n.data)) return n;
    return null;
  };
  const parseCount = (txt) => {
    const m = /^([^\d]*?)(\d[\d,]*(?:\.\d+)?)([\s\S]*)$/.exec(txt);
    if (!m) return null;
    const n = parseFloat(m[2].replace(/,/g, ''));
    if (!Number.isFinite(n)) return null;
    return { pre: m[1], suf: m[3], n, dec: (m[2].split('.')[1] || '').length, grp: m[2].indexOf(',') > -1 };
  };
  const fmt = (c, v) => c.pre + (c.grp
    ? v.toLocaleString('en-US', { minimumFractionDigits: c.dec, maximumFractionDigits: c.dec })
    : v.toFixed(c.dec)) + c.suf;

  const shapesOf = (el) => {
    const all = [...(el.matches(SHAPES) ? [el] : []), ...el.querySelectorAll(SHAPES)];
    return all.filter((s) => getComputedStyle(s).stroke !== 'none');
  };
  const DRAW_ATTRS = ['pathLength', 'stroke-dasharray', 'stroke-dashoffset'];

  // ---- presets: prep (hidden start state) · run (returns anime instance) · stop (revert to final/clean) -------------
  const enter = {
    prep(r) { r.items = r.preset === 'stagger' ? [...r.el.children] : [r.el]; r.items.forEach((it) => hide(it, FROM[r.preset](r.o))); },
    run(r) {
      const from = FROM[r.preset](r.o);
      const p = { duration: r.o.duration, ease: EASE, onComplete: () => r.items.forEach(restore) };
      for (const k in from) p[k] = [from[k], k === 'opacity' || k === 'scale' || k === 'scaleX' ? 1 : 0];
      p.delay = r.items.length > 1 ? stagger(r.o.stagger, { start: r.o.delay }) : r.o.delay;
      r.items.forEach((it) => { it.style.willChange = 'transform, opacity'; });
      return animate(r.items, p);
    },
    stop(r) { r.items.forEach(restore); },
  };
  const PRESETS = {
    count: {
      prep(r) {
        r.node = textNodeWithNumber(r.el);
        r.c = r.node && parseCount(r.node.data);
        if (!r.c) { r.skip = true; return; }
        r.final = r.node.data;
        const s = r.el.style;
        r.lock = { d: s.display, m: s.minWidth, v: s.fontVariantNumeric };
        if (getComputedStyle(r.el).display === 'inline') s.display = 'inline-block';
        s.fontVariantNumeric = 'tabular-nums';
        s.minWidth = Math.ceil(r.el.getBoundingClientRect().width) + 'px';
        r.node.data = fmt(r.c, 0);
      },
      run(r) {
        const st = { v: 0 };
        return animate(st, {
          v: r.c.n, duration: r.o.duration, delay: r.o.delay, ease: 'outExpo',
          onUpdate: () => { r.node.data = fmt(r.c, st.v); },
          onComplete: () => { r.node.data = r.final; },
        });
      },
      stop(r) { if (r.node && r.final != null) r.node.data = r.final; },
    },
    draw: {
      prep(r) {
        r.shapes = shapesOf(r.el);
        if (!r.shapes.length) { r.skip = true; return; }
        r.attrs = r.shapes.map((s) => DRAW_ATTRS.map((a) => s.getAttribute(a)));
        r.drawables = svg.createDrawable(r.shapes);
        utils.set(r.drawables, { draw: '0 0' });
      },
      run(r) {
        return animate(r.drawables, {
          draw: ['0 0', '0 1'], duration: r.o.duration, ease: 'inOutSine',
          delay: stagger(r.o.stagger, { start: r.o.delay }),
          onComplete: () => PRESETS.draw.stop(r),
        });
      },
      stop(r) {
        (r.shapes || []).forEach((s, i) => DRAW_ATTRS.forEach((a, j) => {
          const v = r.attrs[i][j];
          if (v == null) s.removeAttribute(a); else s.setAttribute(a, v);
        }));
      },
    },
    float: {
      loop: true,
      prep() {},
      run(r) {
        const a = r.o.distance, d = r.o.duration;
        r.el.style.willChange = 'transform';
        return animate(r.el, {
          y: [{ to: -a, duration: d / 2, ease: 'outSine' }, { to: a, duration: d, ease: 'inOutSine' }, { to: 0, duration: d / 2, ease: 'inSine' }],
          loop: true, delay: r.o.delay,
        });
      },
      stop(r) { r.el.style.willChange = ''; r.el.style.transform = ''; },
    },
    'pulse-ring': {
      loop: true,
      prep(r) {
        if (r.ring) return;
        if (getComputedStyle(r.el).position === 'static') { r.pos = r.el.style.position; r.el.style.position = 'relative'; }
        const ring = document.createElement('span');
        ring.setAttribute('aria-hidden', 'true');
        ring.setAttribute('data-cro-anime-ring', '');
        ring.style.cssText = 'position:absolute;inset:0;border-radius:inherit;box-sizing:border-box;border:2px solid var(--cro-anime-ring,currentColor);pointer-events:none;opacity:0';
        r.el.appendChild(ring);
        r.ring = ring;
      },
      run(r) {
        const w = Math.max(r.el.offsetWidth, 24);
        r.ring.style.willChange = 'transform, opacity';
        return animate(r.ring, {
          scale: [1, 1 + (2 * r.o.distance) / w], opacity: [0.5, 0],
          duration: r.o.duration, ease: 'outQuad', loop: true, loopDelay: 700, delay: r.o.delay,
        });
      },
      stop(r) {
        if (r.ring) { r.ring.remove(); r.ring = null; }
        if (r.pos != null) { r.el.style.position = r.pos; r.pos = null; }
      },
    },
  };
  for (const k of Object.keys(FROM)) PRESETS[k] = enter;

  // ---- lifecycle ---------------------------------------------------------------------------------------------------
  const kill = (r) => {
    if (r.anim) { try { r.anim.revert(); } catch (e) { /* engine already gone */ } r.anim = null; }
  };
  const settle = (r) => { // final state, no motion
    kill(r);
    PRESETS[r.preset].stop(r);
    if (r.lock) { r.el.style.display = r.lock.d; r.el.style.minWidth = r.lock.m; r.el.style.fontVariantNumeric = r.lock.v; r.lock = null; }
    r.state = 'done';
  };
  const start = (r) => {
    if (r.skip) return;
    try { r.anim = PRESETS[r.preset].run(r); r.state = 'playing'; } catch (e) { settle(r); }
  };

  const onEnter = (r) => {
    if (reduced()) { settle(r); return; }
    const P = PRESETS[r.preset];
    if (P.loop) {
      if (r.state === 'done') { P.prep(r); r.state = 'pending'; }
      if (r.anim) r.anim.play(); else start(r);
    } else if (r.state === 'pending') {
      start(r);
      if (r.o.once) io && io.unobserve(r.el);
    }
  };
  const onLeave = (r) => {
    const P = PRESETS[r.preset];
    if (P.loop) { if (r.anim) r.anim.pause(); }
    else if (!r.o.once && r.state !== 'pending') { kill(r); P.stop(r); P.prep(r); r.state = 'pending'; }
  };

  const io = hasIO ? new IntersectionObserver((entries) => {
    for (const e of entries) {
      const r = recs.get(e.target);
      if (!r) continue;
      if (!e.isIntersecting) { r.visible = false; onLeave(r); continue; }
      if (e.intersectionRatio >= 0.15 || e.intersectionRect.height >= window.innerHeight * 0.2) { r.visible = true; onEnter(r); }
    }
  }, { threshold: [0, 0.01, 0.05, 0.15], rootMargin: '0px 0px -8% 0px' }) : null;

  const wire = (el) => {
    if (recs.has(el)) return;
    const preset = (el.getAttribute('data-cro-anime') || '').trim();
    const P = PRESETS[preset];
    if (!P) return;
    const d = Object.assign({}, DEF.base, DEF[preset]);
    const r = {
      el, preset, state: 'pending', visible: false, anim: null,
      o: { delay: Math.max(0, num(el, 'delay', 0)), duration: Math.max(1, num(el, 'duration', d.duration)),
        stagger: Math.max(0, num(el, 'stagger', d.stagger)), distance: num(el, 'distance', d.distance), once: flag(el, 'once', true) },
    };
    recs.set(el, r);
    if (!io) { r.state = 'done'; return; } // no IntersectionObserver: leave the content as authored
    if (reduced()) { r.state = 'done'; if (P.loop) io.observe(el); return; } // loops stay observed so they can wake if the setting flips
    try { P.prep(r); } catch (e) { settle(r); return; }
    if (r.skip) { r.state = 'done'; return; }
    io.observe(el);
  };
  const drop = (r) => {
    io && io.unobserve(r.el);
    settle(r);
    (r.items || []).forEach((it) => saved.delete(it));
    recs.delete(r.el);
  };

  const scan = (root) => {
    const base = root || document;
    const list = [];
    if (base.matches && base.matches(SEL)) list.push(base);
    if (base.querySelectorAll) base.querySelectorAll(SEL).forEach((el) => list.push(el));
    list.forEach(wire);
  };

  const onMotionPref = () => {
    recs.forEach((r) => {
      if (reduced()) { settle(r); return; }
      const P = PRESETS[r.preset];
      if (P.loop && r.state === 'done') { P.prep(r); r.state = 'pending'; if (r.visible) onEnter(r); }
    });
  };
  if (mq) (mq.addEventListener ? mq.addEventListener('change', onMotionPref) : mq.addListener(onMotionPref));
  window.addEventListener('beforeprint', () => recs.forEach((r) => { if (!PRESETS[r.preset].loop) settle(r); }));

  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    [...recs.values()].forEach((r) => { if (e.target.contains(r.el)) drop(r); });
  });

  // ---- public API --------------------------------------------------------------------------------------------------
  const inView = (el, cb, opts) => { // run cb(el) once when el is ~15 % visible (cb should check croAnime.reduced())
    const once = !opts || opts.once !== false;
    if (!hasIO) { cb(el); return () => {}; }
    const o = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting && (e.intersectionRatio >= 0.15 || e.intersectionRect.height >= window.innerHeight * 0.2)) {
        cb(e.target);
        if (once) o.unobserve(e.target);
      }
    }), { threshold: [0, 0.01, 0.05, 0.15], rootMargin: '0px 0px -8% 0px' });
    o.observe(el);
    return () => o.disconnect();
  };
  window.croAnime = { animate, stagger, createTimeline, svg, utils, scan, reduced, inView };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan()); else scan();
})();
